import { useMemo } from 'react';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import {
  PresentationTemplateType,
  TenantOrgMembershipType,
} from '@/types/schema';
import { formatDateToMonDDYYYYWithTime } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';

import {
  isPresentationTemplateOwnedByTenant,
  isPresentationTemplateSharedFromOrganization,
  isVisibleFirmPresentationTemplate,
} from '../presentationTemplate.utils';
import { useTenantPresentationTemplateTableQuery } from './graphql/TenantPresentationTemplateTable.generated';

export interface TemplatePresentationTemplateTableRowData {
  id: string;
  displayName: string;
  isPinned: boolean;
  type: PresentationTemplateType;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  isOwned: boolean;
  isPublished: boolean;
  canPublish: boolean;
  actions?: null;
}

interface useTenantPresentationTemplateTableDataOutput
  extends ReturnType<typeof useTenantPresentationTemplateTableQuery> {
  rows: TemplatePresentationTemplateTableRowData[];
  /** True when the tenant is the parent of a Luminary organization. */
  isOrganizationParent: boolean;
}

export function useTenantPresentationTemplateTableData(): useTenantPresentationTemplateTableDataOutput {
  const tenant = useTenantDetailsContext();
  const isOrganizationParent =
    tenant.orgMembershipType === TenantOrgMembershipType.Parent;
  const canPublishTenantPresentationTemplates = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_PUBLISH_TENANT_PRESENTATION_TEMPLATES
  );
  const response = useTenantPresentationTemplateTableQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
  });

  const rows = useMemo<TemplatePresentationTemplateTableRowData[]>(() => {
    const templates = getNodes(response.data?.presentationTemplates).filter(
      (template) => isVisibleFirmPresentationTemplate(template, tenant.id)
    );

    return templates.map<TemplatePresentationTemplateTableRowData>((node) => {
      const isOwned = isPresentationTemplateOwnedByTenant(node, tenant.id);
      const isSharedFromOrganization =
        isPresentationTemplateSharedFromOrganization(node, tenant.id);

      return {
        id: node.id,
        displayName: node.displayName,
        isPinned: node.isPinned,
        type: node.type,
        isOwned,
        isPublished: Boolean(node.luminaryOrganizationID),
        // The server remains responsible for authorizing the organization
        // permission represented by this client-side eligibility check.
        canPublish:
          isOrganizationParent &&
          canPublishTenantPresentationTemplates &&
          isOwned &&
          node.type === PresentationTemplateType.TenantTemplate &&
          !node.systemTemplateKind,
        createdBy:
          node.user?.displayName ??
          (isSharedFromOrganization
            ? 'Organization Admin'
            : EMPTY_CONTENT_HYPHEN),
        createdAt: formatDateToMonDDYYYYWithTime(node.createdAt),
        updatedAt: formatDateToMonDDYYYYWithTime(node.updatedAt),
      };
    });
  }, [
    canPublishTenantPresentationTemplates,
    isOrganizationParent,
    response.data,
    tenant.id,
  ]);

  return {
    ...response,
    rows,
    isOrganizationParent,
  };
}
